import React, { forwardRef, memo } from 'react';

import { styled } from '@compiled/react';

import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { getColorMetadata } from './getColorMetadata';

/*  OptionTag component copied from '@atlassian/jira-option-color-picker/src/option-tag/index.tsx'; */

const styles = cssMap({
	tooltipStyles: {
		overflow: 'hidden',
	},
	tagWrapperStyles: {
		display: 'inline-flex',
		justifyContent: 'left',
		alignItems: 'center',
		transition: '0.3s',
		maxWidth: '100%',
		minWidth: '0px',
		minHeight: '0px',
		color: token('color.text'),
	},
	interactiveStyles: {
		'&:hover': { cursor: 'pointer' },
	},
	styledTagsStyles: {
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		borderRadius: token('radius.small'),
		backgroundColor: token('color.background.neutral.subtle'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		paddingRight: token('space.050'),
		paddingLeft: token('space.050'),
		maxWidth: '180px',
	},
});

export type Props = {
	color: string | null | undefined;
	isColoredLabel?: boolean;
	isInteractive?: boolean;
	name: string;
	showTooltip?: boolean;
};

export const OptionTag: React.MemoExoticComponent<
	React.ForwardRefExoticComponent<Props & React.RefAttributes<HTMLElement>>
> = memo(
	forwardRef<HTMLElement, Props>(
		({ name, color, isInteractive = true, showTooltip = false, isColoredLabel }, ref) => {
			const { backgroundColor: tagColor } = getColorMetadata(color);

			const content = (
				<Box
					xcss={cx(styles.tagWrapperStyles, isInteractive && styles.interactiveStyles)}
					aria-label={name}
					testId="option-color-picker.option-tag.field-tag-wrapper"
					ref={ref}
				>
					{isColoredLabel ? (
						<Box
							testId={`option-color-picker.option-tag.${color || 'default'}`}
							xcss={styles.styledTagsStyles}
							style={{ borderColor: tagColor }}
						>
							{name}
						</Box>
					) : (
						<StyledTag
							data-testid={`option-color-picker.option-tag.${color || 'default'}`}
							color={tagColor}
						>
							{name}
						</StyledTag>
					)}
				</Box>
			);

			return showTooltip ? (
				<Box xcss={styles.tooltipStyles}>
					<Tooltip content={name}>{content}</Tooltip>
				</Box>
			) : (
				content
			);
		},
	),
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledTagBase = styled.div<{ color: string }>({
	font: token('font.body'),
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	maxWidth: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledTag = styled(StyledTagBase)<{ color: string }>({
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage -- The token value "2px" and fallback "3px" do not match and can't be replaced automatically.
	borderRadius: token('radius.xsmall', '3px'),
	backgroundColor: 'transparent',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	border: ({ color }) => `${token('border.width')} solid ${color}`,
	paddingRight: token('space.050'),
	paddingLeft: token('space.050'),
});
